<template>
  <div class="select-checklist" :readonly="readonly">
    <label v-if="$attrs.label" class="select-checklist__label">{{ $attrs.label }}</label>
    <span v-if="$attrs.sublabel" class="select-checklist__sublabel">{{ $attrs.sublabel }}</span>
    <InputComponent
      v-if="hasSearch"
      v-model="filter.search"
      @keydown="performQuery"
      :icon="['fad', 'search']"
      :placeholder="$t('search') + '...'"
      class="select-checklist__search"
    />
    <LoaderComponent :value="promises.fetch" theme="default">
      <div class="select-checklist__items">
        <slot />
        <template
          v-if="
            selectAll &&
            itemsComp.length > 1 &&
            !groupBy &&
            itemsComp.some(({ disabled }) => !disabled)
          "
        >
          <CheckboxComponent :value="isSelected()" @input="selectMany" :label="$t('selectAll')" />
          <hr />
        </template>
        <template v-if="groupBy">
          <div
            v-for="(group, groupIndex) in groupComp"
            :key="group.value"
            class="select-checklist__items-group"
          >
            <hr v-if="groupIndex > 0" />
            <div v-if="group.label && groupComp.length > 1" class="mb-1">
              <b>{{ group.label }}</b>
            </div>
            <template
              v-if="
                selectAll && group.items.length > 1 && group.items.some(({ disabled }) => !disabled)
              "
            >
              <CheckboxComponent
                @input="$ev => selectMany($ev, groupBy.path, group.value)"
                :value="isSelected(groupBy.path, group.value)"
                :label="$t('selectAll')"
              />
              <hr />
            </template>
            <CheckboxComponent
              v-for="(item, index) in group.items"
              :value="value || []"
              @input="$ev => $emit('input', $ev)"
              :key="`${group.value}_${index}`"
              :state="item.value"
              :sublabel="item.sublabel"
              :disabled="item.disabled"
            >
              <template #default>
                <span v-html="item.label"></span>
              </template>
            </CheckboxComponent>
          </div>
        </template>
        <template v-else>
          <CheckboxComponent
            v-for="(item, index) in itemsComp"
            :value="value || []"
            @input="$ev => $emit('input', $ev)"
            :key="index"
            :state="item.value"
            :sublabel="item.sublabel"
            :disabled="item.disabled"
          >
            <template #default>
              <span v-html="item.label"></span>
            </template>
          </CheckboxComponent>
        </template>
        <slot v-if="!value || !value.length" name="noresults" :filter="filter" />
      </div>
    </LoaderComponent>
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { LoaderComponent } from 'vue-elder-loader'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { Debounce } from '.././utils'

export default {
  props: {
    default: {
      type: [String, Array],
    },
    search: Boolean,
    readonly: Boolean,
    selectAll: Boolean,
    groupBy: Object,
    value: Array,
    items: {
      type: [String, Array],
      required: true,
    },
  },
  watch: {
    items: {
      handler(val) {
        if (typeof val === 'string') this.internalfetch(this.filter)
      },
      immediate: true,
    },
  },
  data() {
    return {
      fetchedItems: [],
      selected: [],
      promises: {
        fetch: null,
      },
      filter: {
        search: null,
      },
    }
  },
  computed: {
    groupComp() {
      if (!this.groupBy) return
      return Object.values(
        this.itemsComp.reduce((res, item) => {
          let value = this.$path(this.groupBy.path, item)
          if (value in res) res[value].items.push(item)
          else res[value] = { label: this.$path(this.groupBy.label, item), items: [item], value }
          return res
        }, {}),
      )
    },
    itemsComp() {
      return this.items instanceof Array ? this.items : this.fetchedItems
    },
    hasSearch() {
      return this.search && typeof this.items === 'string'
    },
  },
  methods: {
    performQuery: Debounce(function () {
      this.internalfetch(this.filter)
    }, 200),
    internalfetch(filter) {
      if (this.items instanceof Array) return Promise.resolve(this.items)
      return (this.promises.fetch = fetch(`${this.items}?${new URLSearchParams(filter).toString()}`)
        .then(res => res.json())
        .then(data => (this.fetchedItems = data)))
    },
    isSelected(path, value) {
      let items = (path ? this.getGroupedItems(path, value) : this.itemsComp).filter(
        ({ disabled }) => !disabled,
      )

      if (!items.length) return false
      return items.every(item => (this.value || []).includes(item.value))
    },
    getGroupedItems(path, value) {
      return this.itemsComp.filter(i => this.$path(path, i) === value)
    },
    selectMany(state, path, value) {
      let items = (path ? this.getGroupedItems(path, value) : this.itemsComp)
        .filter(({ disabled }) => !disabled)
        .map(({ value }) => value)

      if (!state)
        return this.$emit(
          'input',
          this.value.filter(v => !items.includes(v)),
        )

      return this.$emit('input', path ? [...(this.value || []), ...items] : items)
    },
  },
  created() {
    if (this.default && (!this.value || !this.value.length)) {
      let items = this.default instanceof Array ? this.default : [this.default]
      this.$emit('input', items)
    }
  },
  components: {
    InputComponent,
    CheckboxComponent,
    LoaderComponent,
  },
}
</script>

<style lang="scss">
.select-checklist {
  &__search {
    margin-bottom: 1rem;
  }

  &__items,
  &__items-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.5rem;
  }

  &__label {
    font-weight: bold;
  }

  &__sublabel {
    font-size: 0.9em;
    display: block;
    opacity: 0.5;
  }

  hr {
    width: 100%;
  }
}
</style>
